import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { isAdmin, isSuperAdmin, roleLabel } from "@/lib/roles";
import { UserCircle, Briefcase, ShieldCheck } from "@phosphor-icons/react";

/**
 * View Toggle — pill-style switch between Member view and Admin/Super-admin view.
 * Rendered inside page headers for users with an admin-type role.
 * Hidden entirely for plain members (they don't need a toggle — only one view).
 */
export function ViewToggle({ onDark = false }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  if (!isAdmin(user)) return null;

  const onMemberView = loc.pathname.startsWith("/dashboard");
  const onSystemView = loc.pathname.startsWith("/system");
  const superAdmin = isSuperAdmin(user);

  const goMember = () => nav("/dashboard");
  const goAdmin = () => nav("/admin");
  const goSystem = () => nav("/system");

  const base =
    "flex items-center gap-1 px-2 sm:px-3 py-1 rounded-lg text-xs font-medium transition-colors";
  const active = onDark
    ? "bg-white text-slate-900"
    : "bg-primary text-primary-foreground";
  const inactive = onDark
    ? "text-white/70 hover:text-white hover:bg-white/10"
    : "text-muted-foreground hover:text-foreground hover:bg-muted";

  const trackCls = onDark
    ? "inline-flex items-center p-0.5 rounded-xl bg-white/10 border border-white/10"
    : "inline-flex items-center p-0.5 rounded-xl bg-muted border border-border";

  return (
    <div className={trackCls} role="tablist" data-testid="view-toggle">
      <button
        role="tab"
        aria-selected={onMemberView}
        onClick={goMember}
        className={`${base} ${onMemberView ? active : inactive}`}
        data-testid="view-toggle-member"
      >
        <UserCircle className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Member</span>
      </button>
      <button
        role="tab"
        aria-selected={!onMemberView && !onSystemView}
        onClick={goAdmin}
        className={`${base} ${!onMemberView && !onSystemView ? active : inactive}`}
        data-testid="view-toggle-admin"
      >
        <Briefcase className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{roleLabel(user.role)}</span>
        <span className="sm:hidden">Admin</span>
      </button>
      {superAdmin && (
        <button
          role="tab"
          aria-selected={onSystemView}
          onClick={goSystem}
          className={`${base} ${onSystemView ? active : inactive}`}
          data-testid="view-toggle-system"
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">System</span>
        </button>
      )}
    </div>
  );
}
